import type { MapMouseEvent, MapRef } from 'react-map-gl/mapbox'
import type { IAreaBoundary } from '@/entities/destination'
import type { MapCoordinate } from '@/features/map-drawing'
import { MapPin, Pencil, Save, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Map, { Layer, Marker, NavigationControl, Source } from 'react-map-gl/mapbox'
import {
  appendCoordinate,
  areaBoundaryToMapCoordinates,
  createBoundaryGeoJson,
  getBoundaryBounds,
  mapCoordinatesToAreaBoundary,
  mergeBounds,
  midpoint,
  removeCoordinateAt,
  updateCoordinateAt,
} from '@/features/map-drawing'
import { UI_THEME_COLORS } from '@/shared/constants/theme-colors'
import { cn } from '@/shared/libs/cn'
import { Button } from '@/shared/ui/button'
import 'mapbox-gl/dist/mapbox-gl.css'

type DrawMode = 'idle' | 'draw-polygon' | 'set-center'

interface DestinationMapPickerProps {
  /** GeoJSON polygon: { type: 'Polygon', coordinates: [[[lng,lat],...]] } */
  areaBoundary: IAreaBoundary | null
  /** GeoJSON center point: [lng, lat] */
  centerPoint: [number, number] | null
  onAreaBoundaryChange: (boundary: IAreaBoundary | null) => void
  onCenterPointChange: (point: [number, number] | null) => void
  /** Called on Save with fresh values — use to trigger API patch in edit mode */
  onSaveMap?: (boundary: IAreaBoundary | null, centerPoint: [number, number] | null) => void
  readonly?: boolean
  /** Disables the Save button while the parent's save request is in flight */
  saving?: boolean
}

const DEFAULT_VIEW = { latitude: 16.047079, longitude: 108.20623, zoom: 6 }
const FIT_BOUNDS_OPTIONS = { padding: 60, duration: 0, maxZoom: 16 } as const
const CENTER_POINT_ZOOM = 18
const VERTEX_COLOR = '#16A34A'
const MIDPOINT_COLOR = '#86EFAC'

export function DestinationMapPicker({
  areaBoundary,
  centerPoint,
  onAreaBoundaryChange,
  onCenterPointChange,
  onSaveMap,
  readonly = false,
  saving = false,
}: DestinationMapPickerProps) {
  const { t } = useTranslation()
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN
  const mapStyle = import.meta.env.VITE_MAPBOX_STYLE || 'mapbox://styles/mapbox/streets-v12'
  const mapRef = useRef<MapRef>(null)
  const [viewState, setViewState] = useState(DEFAULT_VIEW)
  const [mode, setMode] = useState<DrawMode>('idle')
  const isDraggingRef = useRef(false)
  const initialCameraApplied = useRef(false)
  const centerCameraApplied = useRef(false)

  // Draft coords accumulate all clicks regardless of count.
  // areaBoundary requires ≥3 pts to be non-null, so we can't derive from it directly
  // or the first 2 clicks silently reset back to [].
  const [draftCoords, setDraftCoords] = useState<MapCoordinate[]>(() =>
    areaBoundaryToMapCoordinates(areaBoundary),
  )

  // Draft center point — updated locally; committed to parent only on Save
  const [draftCenterPoint, setDraftCenterPoint] = useState<[number, number] | null>(centerPoint)

  // Sync external areaBoundary → draftCoords only when draftCoords is empty
  // (covers async data load for edit mode). Skips sync while user is actively drawing.
  useEffect(() => {
    if (areaBoundary !== null) {
      setDraftCoords((prev) =>
        prev.length === 0 ? areaBoundaryToMapCoordinates(areaBoundary) : prev,
      )
    }
  }, [areaBoundary])

  // Sync external centerPoint → draftCenterPoint only when draft is null (async data load)
  useEffect(() => {
    if (centerPoint !== null) {
      setDraftCenterPoint((prev) => prev ?? centerPoint)
    }
  }, [centerPoint])

  const boundaryGeoJson = useMemo(() => {
    const boundary = mapCoordinatesToAreaBoundary(draftCoords)
    return createBoundaryGeoJson(boundary)
  }, [draftCoords])
  const boundaryBounds = useMemo(() => getBoundaryBounds(areaBoundary), [areaBoundary])

  const isDirty = useMemo(() => {
    const draftBoundary = mapCoordinatesToAreaBoundary(draftCoords)
    return (
      JSON.stringify(draftBoundary) !== JSON.stringify(areaBoundary) ||
      JSON.stringify(draftCenterPoint) !== JSON.stringify(centerPoint)
    )
  }, [draftCoords, draftCenterPoint, areaBoundary, centerPoint])

  // Draft center in GeoJSON [lng, lat] convention
  const centerLng = draftCenterPoint?.[0] ?? null
  const centerLat = draftCenterPoint?.[1] ?? null

  // Apply initial camera once — either at mount (sync data) or when async data first arrives.
  // The ref flag ensures we only zoom once even though the effect re-runs as data loads.
  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    const applyCamera = () => {
      if (centerPoint && !centerCameraApplied.current) {
        setViewState({
          latitude: centerPoint[1],
          longitude: centerPoint[0],
          zoom: CENTER_POINT_ZOOM,
        })
        map.jumpTo({ center: [centerPoint[0], centerPoint[1]], zoom: CENTER_POINT_ZOOM })
        initialCameraApplied.current = true
        centerCameraApplied.current = true
        return
      }

      if (initialCameraApplied.current) return

      const bounds = mergeBounds([boundaryBounds])
      if (bounds) {
        map.fitBounds(bounds, FIT_BOUNDS_OPTIONS)
        initialCameraApplied.current = true
      }
    }

    if (map.loaded()) {
      applyCamera()
    } else {
      map.once('load', applyCamera)
    }
    return () => {
      map.off('load', applyCamera)
    }
  }, [areaBoundary, centerPoint, boundaryBounds])

  function handleSave() {
    if (saving) return
    const boundary = mapCoordinatesToAreaBoundary(draftCoords)
    onAreaBoundaryChange(boundary)
    onCenterPointChange(draftCenterPoint)
    onSaveMap?.(boundary, draftCenterPoint)
  }

  function handleMapClick(event: MapMouseEvent) {
    if (readonly) return
    if (isDraggingRef.current) {
      isDraggingRef.current = false
      return
    }
    const { lat, lng } = event.lngLat
    if (mode === 'draw-polygon') {
      setDraftCoords((next) => appendCoordinate(next, [lat, lng]))
    } else if (mode === 'set-center') {
      setDraftCenterPoint([lng, lat])
      setMode('idle')
    }
  }

  function handleVertexDragEnd(index: number, lat: number, lng: number) {
    isDraggingRef.current = true
    setDraftCoords((prev) => updateCoordinateAt(prev, index, [lat, lng]))
  }

  function handleVertexDelete(index: number) {
    setDraftCoords((prev) => removeCoordinateAt(prev, index))
  }

  function handleMidpointClick(insertIndex: number, point: MapCoordinate) {
    setDraftCoords((prev) => {
      const next = prev.slice()
      next.splice(insertIndex, 0, point)
      return next
    })
  }

  function renderFallback() {
    return (
      <div
        style={{
          height: 400,
          display: 'grid',
          placeItems: 'center',
          background: UI_THEME_COLORS.poiMapGradient,
        }}
      >
        <p className="text-sm text-zinc-500">{t('poi.map.missingTokenDescription')}</p>
      </div>
    )
  }

  function renderToolbar() {
    if (readonly) return null
    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-zinc-100 bg-zinc-50">
        <Button
          type="button"
          variant={mode === 'draw-polygon' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode(mode === 'draw-polygon' ? 'idle' : 'draw-polygon')}
        >
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          {mode === 'draw-polygon'
            ? t('destination.map.stopDrawing', { defaultValue: 'Stop Drawing' })
            : t('destination.map.drawBoundary', { defaultValue: 'Draw Boundary' })}
        </Button>
        <Button
          type="button"
          variant={mode === 'set-center' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode(mode === 'set-center' ? 'idle' : 'set-center')}
        >
          <MapPin className="mr-1.5 h-3.5 w-3.5" />
          {mode === 'set-center'
            ? t('destination.map.cancelSetCenter', { defaultValue: 'Cancel' })
            : t('destination.map.setCenter', { defaultValue: 'Set Center' })}
        </Button>
        {draftCoords.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setDraftCoords([])
              setMode('idle')
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            {t('destination.map.clearBoundary', { defaultValue: 'Clear Boundary' })}
          </Button>
        )}
        {draftCenterPoint && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDraftCenterPoint(null)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            {t('destination.map.clearCenter', { defaultValue: 'Clear Center' })}
          </Button>
        )}
        <span className="ml-auto text-xs text-zinc-500">
          {t('destination.map.vertices', { defaultValue: 'Vertices' })}
          {': '}
          {draftCoords.length}
        </span>
      </div>
    )
  }

  function renderVertexMarkers() {
    if (mode !== 'draw-polygon') return null
    return draftCoords.map((coord, index) => (
      <Marker
        key={`vertex-${index}`}
        longitude={coord[1]}
        latitude={coord[0]}
        draggable={!readonly}
        anchor="center"
        onDragEnd={(event) => handleVertexDragEnd(index, event.lngLat.lat, event.lngLat.lng)}
      >
        <div className="group relative flex items-center justify-center">
          <div
            className="h-6 w-6 rounded-full border-2 border-white text-[10px] font-bold shadow-md flex items-center justify-center text-white"
            style={{ backgroundColor: VERTEX_COLOR }}
          >
            {index + 1}
          </div>
          {!readonly && (
            <button
              type="button"
              className="absolute -top-2.5 -right-2.5 hidden group-hover:flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold shadow"
              onClick={(e) => {
                e.stopPropagation()
                handleVertexDelete(index)
              }}
              title={t('destination.map.deleteVertex', { defaultValue: 'Delete vertex' })}
            >
              ✕
            </button>
          )}
        </div>
      </Marker>
    ))
  }

  function renderMidpointMarkers() {
    if (mode !== 'draw-polygon' || draftCoords.length < 2) return null
    const markers = []
    for (let i = 0; i < draftCoords.length - 1; i++) {
      const a = draftCoords[i]
      const b = draftCoords[i + 1]
      if (!a || !b) continue
      const mid = midpoint(a, b)
      markers.push(
        <Marker key={`mid-${i}`} longitude={mid[1]} latitude={mid[0]} anchor="center">
          <button
            type="button"
            className="flex h-4 w-4 items-center justify-center rounded-full border border-white text-[9px] font-bold text-white shadow opacity-60 hover:opacity-100 transition-opacity"
            style={{ backgroundColor: MIDPOINT_COLOR }}
            onClick={(e) => {
              e.stopPropagation()
              handleMidpointClick(i + 1, mid)
            }}
            title={t('destination.map.addVertex', { defaultValue: 'Add vertex' })}
          >
            +
          </button>
        </Marker>,
      )
    }
    return markers
  }

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-zinc-800">
        {t('destination.locationBoundary', { defaultValue: 'Location and boundary' })}{' '}
        <span className="text-red-500">*</span>
      </p>
      <div className="border border-zinc-200 rounded-xl overflow-hidden">
        {renderToolbar()}
        {mode === 'draw-polygon' && (
          <div className="px-3 py-1.5 bg-green-50 border-b border-green-100">
            <p className="text-xs text-green-700">
              {t('destination.map.drawBoundaryHint', {
                defaultValue:
                  'Click on the map to add boundary vertices. Drag vertices to adjust. Click + between vertices to insert.',
              })}
            </p>
          </div>
        )}
        {mode === 'set-center' && (
          <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100">
            <p className="text-xs text-blue-700">
              {t('destination.map.setCenterHint', {
                defaultValue: 'Click on the map to place the center point.',
              })}
            </p>
          </div>
        )}
        {!mapboxToken ? (
          renderFallback()
        ) : (
          <div className="relative h-[400px] lg:h-[500px]">
            <Map
              ref={mapRef}
              mapboxAccessToken={mapboxToken}
              mapStyle={mapStyle}
              longitude={viewState.longitude}
              latitude={viewState.latitude}
              zoom={viewState.zoom}
              onMove={(event) =>
                setViewState({
                  latitude: event.viewState.latitude,
                  longitude: event.viewState.longitude,
                  zoom: event.viewState.zoom,
                })
              }
              onClick={handleMapClick}
              {...(mode !== 'idle' ? { cursor: 'crosshair' } : {})}
              reuseMaps
              style={{ width: '100%', height: '100%' }}
            >
              <NavigationControl position="top-right" />

              {boundaryGeoJson && (
                <Source id="dest-boundary" type="geojson" data={boundaryGeoJson}>
                  <Layer
                    id="dest-boundary-fill"
                    type="fill"
                    paint={{ 'fill-color': '#22C55E', 'fill-opacity': 0.15 }}
                  />
                  <Layer
                    id="dest-boundary-outline"
                    type="line"
                    layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                    paint={{ 'line-color': '#16A34A', 'line-width': 2 }}
                  />
                </Source>
              )}

              {renderMidpointMarkers()}
              {renderVertexMarkers()}

              {centerLat !== null && centerLng !== null && (
                <Marker
                  longitude={centerLng}
                  latitude={centerLat}
                  draggable={!readonly}
                  anchor="bottom"
                  onDragEnd={(event) => setDraftCenterPoint([event.lngLat.lng, event.lngLat.lat])}
                >
                  <div className={cn('flex flex-col items-center', !readonly && 'cursor-move')}>
                    <div className="h-3 w-3 rounded-full bg-red-600 border-2 border-white shadow-md" />
                    <div className="w-0.5 h-3 bg-red-600" />
                  </div>
                </Marker>
              )}
            </Map>
          </div>
        )}
      </div>
      {!readonly && isDirty && (
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-xs text-amber-700">
            {t('destination.map.unsavedChanges', {
              defaultValue: 'Bản đồ có thay đổi chưa được lưu vào form.',
            })}
          </p>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
          >
            <Save className="mr-1.5 h-3.5 w-3.5" />
            {t('destination.map.saveMap', { defaultValue: 'Lưu bản đồ' })}
          </Button>
        </div>
      )}
      {draftCenterPoint && (
        <p className="text-xs text-zinc-500">
          {t('destination.map.centerCoords', { defaultValue: 'Center' })}
          {': '}
          {draftCenterPoint[1].toFixed(6)}
          {', '}
          {draftCenterPoint[0].toFixed(6)}
        </p>
      )}
    </div>
  )
}
